import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { switchMap, catchError, tap  } from 'rxjs/operators';
import { forkJoin, of, EMPTY, from } from 'rxjs';
import { ServerApiService } from "app/services/server-api.service";
import { Router } from "@angular/router";
import { AuthService } from "app/services/auth/auth.service";
import { IsLoggedService } from "app/services/data-share/is-logged.service";
import { User } from "app/models/interfaces/user/user";
import { Token } from "app/models/interfaces/token";
import {SocketService} from "app/services/socket.service"
import { AbstractControl, FormControl, ValidationErrors, Validators } from '@angular/forms';

@Component({
  moduleId: module.id,
  selector: "login-cmp",
  templateUrl: "login.component.html",
  styleUrls: ["login.component.css"],
  providers: [ServerApiService],
})
export class LoginComponent implements OnInit {
  @ViewChild('loginDialog', { static: true }) loginDialog!: ElementRef;
  @ViewChild('confirmationDialog', { static: true }) confirmationDialog!: ElementRef;
  user = "";
  password = "";
  loginError = false;
  loginErrorText = "";
  isLoaded: boolean = false;
  isSharedLocation: boolean = false;
  locationDenied: boolean = false;
  locationMessage: string = "";
  isFirstAcknowledged: boolean = true;
  isAgree: boolean = false;
  private pendingLoginUser: User | null = null;
  private policyAcceptedText = `אני הח"מ מצהיר/ה ומאשר/ת בזאת כי: קראתי בעיון את חוזה הלימודים ותקנון המכללה, לרבות כל התנאים, ההתחייבויות, הנהלים והמדיניות המפורטים בהם.
      ידוע לי כי חוזה הלימודים מהווה הסכם מחייב ביני לבין המכללה. הבנתי את מלוא תוכנם ומשמעותם המשפטית והמעשית של המסמכים האמורים.
      אני מסכים/ה לכל התנאים המפורטים בחוזה הלימודים ובתקנון ללא הסתייגות.
      אני מאשר/ת בסימון תיבת האישור כי הצהרתי לעיל אמת וכי הסכמתי תקפה ומחייבת לכל דבר ועניין.`
  
  options = {
    enableHighAccuracy: false, // network/Wi-Fi location is enough for a permission gate and is much faster than forcing GPS-level accuracy
    timeout: 5000, // The maximum length of time (in milliseconds) the device is allowed to take in order to return a position
    maximumAge: 300000  // accept a cached fix up to 5 minutes old instead of forcing a fresh lookup on every login
  };
  
  geoLocationData: {latitude: number, longitude: number}
  constructor(
    private serverApiService: ServerApiService,
    private IsLoggedService: IsLoggedService,
    private AuthService: AuthService,
    private router: Router,
    socketService: SocketService
  ) {}

  idControl = new FormControl('', [
    Validators.required,
    this.israeliIdValidator
  ]);

  ngOnInit() {
    //console.log('LoginComponent:',this.user);
    this.IsLoggedService.setUserProfile("Login");
    this.serverApiService.checkTokenValidity().subscribe(
      async (token: Token) => {
        if (token.status) {
          // The token cookie alone is NOT enough to enter: the auto-login (refresh /
          // direct-URL) path must pass the same geolocation gate as the manual login,
          // otherwise denying location can be bypassed by simply reloading the page.
          // We must fetch the user FIRST, because the gate is now per-student
          // (geo_exempt students skip it) and that flag only arrives with the record.
          this.serverApiService.loginWithToken().subscribe(
            async (data) => {
              // bring to format
              let theuser: any = { user: data[0] };
              let user: User = theuser;
              const allowed = await this.passesLocationGate(user);
              if (!allowed) {
                // Block auto-login. isAuth stays false, so the AuthGuard keeps every
                // protected route out until the user allows location.
                this.loginError = true;
                this.loginErrorText = this.locationMessage;
                this.isLoaded = true;
                return;
              }
              // **************** */
              // The auth cookie is issued by POST /login BEFORE the policy/ID dialog runs,
              // so a valid cookie alone must NOT grant entry: the auto-login (refresh /
              // direct-URL) path has to enforce the same policy-acceptance + ID check as
              // the manual login. Without this, entering a wrong ID at the dialog can be
              // bypassed by simply reloading the page.
              this.serverApiService.isPolicyAccepted(user.user.studentID).subscribe(
                (ans) => {
                  if (!ans.ans) {
                    // Policy not accepted yet -> require the ID confirmation again instead
                    // of logging in. isAuth stays false until completeLogin runs.
                    this.pendingLoginUser = user;
                    this.isAgree = false;
                    this.idControl.reset('');
                    this.confirmationDialog.nativeElement.showModal();
                    this.isLoaded = true;
                    return;
                  }
                  this.updateStateSuccsess(user);
                  this.isLoaded = true;
                },
                (err) => this.updateStateFail(err)
              );
            },
            (err) => this.updateStateFail(err)
          );
        }
        if (!token.status) {
          this.isLoaded = true;
        }
      },
      (err) => {
        //console.log("server error");
        this.isLoaded = true;
      }
    );
       // this.confirmationDialog.nativeElement.showModal();
  }

  // studentID lives in a VARCHAR(9) column, so the same person can be stored as
  // "012345678" or "12345678" (a lost leading zero). Normalize both the entered
  // value and the stored value to a canonical 9-digit, zero-padded, digits-only
  // form before comparing, otherwise a correct ID is wrongly flagged as a mismatch.
  private normalizeId(value: any): string {
    return String(value ?? '').replace(/\D/g, '').padStart(9, '0');
  }

  isConfirmationIdMatchingStudent(): boolean {
    if (!this.pendingLoginUser) {
    //  console.log('[idConfirm] isConfirmationIdMatchingStudent -> no pendingLoginUser');
      return false;
    }
    const rawEntered = this.idControl.value;
    const rawStudent = this.pendingLoginUser.user.studentID;
    const enteredId = this.normalizeId(rawEntered);
    const studentId = this.normalizeId(rawStudent);
    const isMatch = enteredId === studentId;
    // console.log('[idConfirm] match check', {
    //   rawEntered,
    //   rawStudent,
    //   normalizedEntered: enteredId,
    //   normalizedStudent: studentId,
    //   isMatch,
    // });
    return isMatch;
  }

  confirmPolicyAndContinue() {
    // console.log('[idConfirm] confirmPolicyAndContinue called', {
    //   hasPendingUser: !!this.pendingLoginUser,
    //   isAgree: this.isAgree,
    //   idControlValue: this.idControl.value,
    //   idControlValid: this.idControl.valid,
    //   idControlInvalid: this.idControl.invalid,
    //   idControlErrors: this.idControl.errors,
    // });

    if (!this.pendingLoginUser) {
   //   console.log('[idConfirm] aborted -> no pendingLoginUser');
      return;
    }
    if (!this.isAgree || this.idControl.invalid) {
    //  console.log('[idConfirm] aborted -> not agreed or id control invalid', {
      //   isAgree: this.isAgree,
      //   idControlInvalid: this.idControl.invalid,
      //   idControlErrors: this.idControl.errors,
      // });
      this.idControl.markAsTouched();
      return;
    }
    if (!this.isConfirmationIdMatchingStudent()) {
     // console.log('[idConfirm] aborted -> id mismatch, setting idMismatch error');
      this.idControl.setErrors({ idMismatch: true });
      return;
    }

  //  console.log('[idConfirm] all checks passed -> posting policy acceptance');

    const user = this.pendingLoginUser;
    this.serverApiService.postPolicyAccepted({
      studentId: user.user.studentID,
      firstName: user.user.firstName,
      familyName: user.user.familyName,
      acceptedText: this.policyAcceptedText,
    }).subscribe(
      () => {
       // console.log('postPolicyAccepted success');
        this.closeConfirmationModal();
        this.pendingLoginUser = null;
        this.completeLogin(user);
      },
      () => {
        this.loginError = true;
        this.loginErrorText = "Could not save policy confirmation. Please try again.";
      }
    );
  }

  israeliIdValidator(control: AbstractControl): ValidationErrors | null {
    const raw = String(control.value ?? '').trim();
    if (!raw) return null;
    // Format-only check (1-9 digits). We intentionally do NOT run the Israeli ת״ז
    // checksum here: studentID is a VARCHAR(9) internal identifier and is not
    // guaranteed to be a checksum-valid Israeli ID, so a checksum gate would reject
    // legitimate students before they can confirm. The real identity check is the
    // studentID match in isConfirmationIdMatchingStudent().
    if (!/^\d{1,9}$/.test(raw)) {
      return { invalidId: true };
    }
    return null;
  }

  onlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  updateStateSuccsess(data: User) {
    this.IsLoggedService.setUserLoggedIn(true);
    this.AuthService.login();
    this.IsLoggedService.setUserData(data);
    if (data.user.role === 1) {
      this.IsLoggedService.setUserProfile("Student");
      this.router.navigate(["/table-list"]);
    } else if (data.user.role === 2) {
      this.IsLoggedService.setUserProfile("Teacher");
      this.router.navigate(["/teacher-list"]);
    }else if (data.user.role === 4) {
      this.IsLoggedService.setUserProfile("Guest");
      this.router.navigate(["/solaredge-course-list"]);
    }if (data.user.role === 2000) {
      this.IsLoggedService.setUserProfile("Admin");
      this.router.navigate(["/admin-page"]);
    }
  }

  updateStateFail(error: any) {
    this.IsLoggedService.setUserLoggedIn(false);
    this.IsLoggedService.setUserProfile("Login");
    this.AuthService.logout();
    //console.log("ERROR:", error);
    this.loginErrorText = "Login error " + error.error.text;
    this.loginError = true;
    this.user = "";
    this.password = "";
  }

  async getLogin(event?: Event) {
    event?.preventDefault();
    //console.log('fetchLoginNew in getLogin cmp on send ')
    this.loginError = false;
    this.locationDenied = false;
    this.locationMessage = "";
    if (!this.user.trim() && !this.password.trim()) {
      return;
    }
      //console.log(' /* geolocation is available */');
    // Authenticate FIRST, then gate. The geolocation check needs the student
    // record to know whether this student is exempt (geo_exempt), so we can no
    // longer gate before login. /login issues the auth cookie before this runs,
    // so if a non-exempt student fails the gate we must logOut() to invalidate
    // that cookie (otherwise the auto-login path could let them back in).
    this.serverApiService.fetchLoginNew(this.user, this.password).pipe(
    //-   tap((data: User) => console.log('fetchLoginNew data:', data)),
      switchMap((data: User) => from(this.passesLocationGate(data)).pipe(
        switchMap((allowed) => {
          if (!allowed) {
            this.serverApiService.logOut().subscribe();
            this.loginError = true;
            this.loginErrorText = this.locationMessage;
            return EMPTY;
          }
          return this.afterLoginAuthorized(data);
        })
      )),
      catchError((error) => {
        this.updateStateFail(error);
        return of(null);
      })
    ).subscribe();
  }

  // Runs the geolocation permission gate unless the student is flagged
  // geo_exempt. Exempt students (and any non-student role, which never carried
  // the flag) are let through without a location check.
  private async passesLocationGate(data: User): Promise<boolean> {
    if (data.user.geo_exempt) {
      return true;
    }
    return this.ensureLocationAllowed();
  }

  // Policy/ID-confirmation step for students, straight completeLogin otherwise.
  // Mirrors the auto-login path in ngOnInit.
  private afterLoginAuthorized(data: User) {
    if (data.user.role != 1) {
      this.completeLogin(data);
      return EMPTY;
    }
    return this.serverApiService.isPolicyAccepted(data.user.studentID).pipe(
          switchMap((ans) => {
           // console.log('isPolicyAccepted answer:', ans);
            
            if (!ans.ans) {
              this.pendingLoginUser = data;
              this.isAgree = false;
              this.idControl.reset('');
              this.confirmationDialog.nativeElement.showModal();
              return EMPTY;
            }
            this.completeLogin(data);
            return EMPTY;
          })
        );
  }

  private completeLogin(data: User) {
    this.updateStateSuccsess(data);
    const internship$ = this.serverApiService
      .isStudentFinishedPercentage(data.user.studentID, 'RT_Student_Portal', 0.8)
      .pipe(tap((ans) => this.IsLoggedService.updateIsInternship(ans.data)));
    const placement$ = this.serverApiService
      .isStudentFinishedPercentage(data.user.studentID, 'RT_Student_Portal', 0.9)
      .pipe(tap((ans) => this.IsLoggedService.updateIsPlacement(ans.data)));
    const graduation$ = this.serverApiService
      .isStudentFinishedPercentage(data.user.studentID, 'RT_Student_Portal', 1)
      .pipe(tap((ans) => this.IsLoggedService.updateIsGraduationCertificate(ans.data)));
    forkJoin([internship$, placement$, graduation$]).subscribe();
  }
  closeModal(){
    this.loginDialog.nativeElement.close();
  }
  closeConfirmationModal(){
    this.confirmationDialog.nativeElement.close();
  }

  // Helper: returns true only if permission is granted AND we can read position
private getCurrentPosition$(): Promise<{ latitude: number; longitude: number }> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      reject,
      this.options
    );
  });
}



  private async ensureLocationAllowed(): Promise<boolean> {
    // Geolocation API missing
    if (!("geolocation" in navigator)) {
      this.locationMessage = "Your browser doesn't support location services.";
      return false;
    }
    // The actual location coordinates are captured separately by the socket flow
    // (backend emits `getGeoLocation` -> SocketService responds with `geoLocationData`),
    // so login only needs to enforce the permission gate, not fetch a position.
    // If the Permissions API exists, decide from its state without a (slow) position fetch:
    //   - granted -> proceed instantly, no fetch
    //   - denied  -> block login
    //   - prompt  -> fall through to a one-time getCurrentPosition() to pop the browser dialog
    const navAny = navigator as any;
    if (navAny.permissions?.query) {
      try {
        const status = await navAny.permissions.query({ name: "geolocation" });
        if (status.state === "granted") {
          return true;
        }
        if (status.state === "denied") {
          this.locationMessage =
            "Location permission is blocked in your browser. Please enable it to continue.";
          return false;
        }
        // state === "prompt": fall through to request location so the popup is shown
      } catch {
        // Permissions API failed -> fall through and request location directly
      }
    }
    // Only reached for first-time ("prompt") users or browsers without the Permissions API.
    // This shows the permission dialog; we don't keep the coordinates here since the
    // socket flow is the source of truth for the stored location.
    try {
      await this.getCurrentPosition$();
      return true;
    }catch (err) {
      const code = typeof err === "object" && err !== null && "code" in err
        ? (err as { code?: number }).code
        : undefined;
      this.locationMessage =
        code === 1
          ? "You must allow location sharing to log in."
          : "Couldn't get location. Please allow location and try again.";
      return false;
    }
  }

  onCheckboxAgree(isChecked: boolean){
    this.isAgree = isChecked;
  }
  //****************/ old geolocation code:
  // const options = {
  //   enableHighAccuracy: true,
  //   timeout: 5000,
  //   maximumAge: 300000,
  // };
  
  // success(pos) {
  //   const crd = pos.coords;
  
  //   //console.log("Your current position is:");
  //   //console.log(`Latitude : ${crd.latitude}`);
  //   //console.log(`Longitude: ${crd.longitude}`);
  //   //console.log(`More or less ${crd.accuracy} meters.`);
  // }
  
  // error(err) {
  //   console.warn(`ERROR(${err.code}): ${err.message}`);
  // }
  
  // navigator.geolocation.getCurrentPosition(success, error, options);
}
